import { useCallback, useState } from "react";
import appLogger from "../../../infra/logger/appLogger";
import { AnalyticsEvents, useAnalytics } from "../../analytics";
import { useAppDispatch, useAppSelector } from "../../hook";
import { useTranslation } from "../../localization/hooks";
import { ModerationFlows, useModerationErrors } from "../../miscellaneous";
import useUserInfo from "../useUserInfo";
import { createPersona, deletePersonas, makeDefaultPersona, setSelectedPersona, updatePersona, updatePersonaIdInConversation } from "./personaSlice";
import { PersonaErrorType } from "./types";
const personaLogger = appLogger.withTag("Persona");
export default function usePersona() {
    const dispatch = useAppDispatch();
    const { userId } = useUserInfo();
    const { logEvent } = useAnalytics();
    const { getMessage: getModerationErrorMessage } = useModerationErrors();
    const { t } = useTranslation();
    const { selectedPersona, loading, defaultPersonaId, personas, personaIdsMap } = useAppSelector((state) => state.userPersona);
    const [isDeletingPersonaStarted, setIsDeletingPersonaStarted] = useState(false);
    const [selectedPersonaIds, setSelectedPersonaIds] = useState([]);
    const [personaError, setPersonaError] = useState(null);
    const [success, setSuccess] = useState(false);
    const getDefaultPersona = () => {
        if (defaultPersonaId) {
            return personas?.find((persona) => persona.id === defaultPersonaId);
        }
        return null;
    };
    const onUserStartDeletingPersona = () => {
        setIsDeletingPersonaStarted(true);
    };
    const onUserConfirmDeletingPersona = useCallback(async () => {
        try {
            if (!selectedPersonaIds.length) {
                setPersonaError({
                    errorType: PersonaErrorType.NO_PERSONA_SELECTED,
                });
            }
            else {
                await dispatch(deletePersonas(selectedPersonaIds)).unwrap();
                setSuccess(true);
            }
        }
        catch (error) {
            const apiError = error;
            setPersonaError({
                errorType: PersonaErrorType.DELETE_PERSONA,
                errorMessage: apiError?.message,
                errorCode: apiError?.errorCode,
            });
        }
        finally {
            resetDeleteStates();
        }
    }, [selectedPersonaIds]);
    const onUserCancelDeletingPersona = () => {
        resetDeleteStates();
    };
    const getUserPersonaById = useCallback((id) => {
        if (id in personaIdsMap) {
            return personaIdsMap[id];
        }
        return null;
    }, [personaIdsMap]);
    const resetDeleteStates = () => {
        setIsDeletingPersonaStarted(false);
        setSelectedPersonaIds([]);
    };
    /** backward compatible for native - to be removed after native done with error handling. Please use onUserCreatePersonaV2 */
    const onUserCreatePersona = async (data) => {
        try {
            data.is_default = data.is_default || false;
            const result = await dispatch(createPersona(data)).unwrap();
            if (result?.id) {
                logEvent(AnalyticsEvents.PERSONA_CREATE, {
                    user_id: userId,
                    persona_id: result.id,
                });
            }
            setSuccess(true);
        }
        catch (error) {
            const apiError = error;
            setPersonaError({
                errorType: PersonaErrorType.CREATE_PERSONA,
                errorMessage: apiError?.message,
                errorCode: apiError?.errorCode,
            });
        }
    };
    const onUserCreatePersonaV2 = async (data) => {
        try {
            setPersonaError(null);
            data.is_default = data.is_default || false;
            const result = await dispatch(createPersona(data)).unwrap();
            if (result?.id) {
                logEvent(AnalyticsEvents.PERSONA_CREATE, {
                    user_id: userId,
                    persona_id: result.id,
                });
            }
            setSuccess(true);
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorCode = apiError?.errorCode;
            const moderationErrorMessage = getModerationErrorMessage({
                errorCode,
                skipDefaultMessage: true,
                errorContext: { flow: ModerationFlows.CREATE_PERSONA },
            });
            const errorMessage = moderationErrorMessage || apiError?.message || t("error:fallbackError");
            setPersonaError({
                errorMessage,
                errorCode,
                errorType: PersonaErrorType.CREATE_PERSONA,
            });
            personaLogger.error(`Error creating persona: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    };
    /** backward compatible for native - to be removed after native done with error handling. Please use onUserUpdatePersonaV2 */
    const onUserUpdatePersona = async (id, params) => {
        try {
            params.is_default = params?.is_default || id === defaultPersonaId || false;
            await dispatch(updatePersona({ id, params })).unwrap();
            setSuccess(true);
        }
        catch (error) {
            const apiError = error;
            setPersonaError({
                errorType: PersonaErrorType.UPDATE_PERSONA,
                errorMessage: apiError?.message,
                errorCode: apiError?.errorCode,
            });
        }
    };
    const onUserUpdatePersonaV2 = async (id, params) => {
        try {
            setPersonaError(null);
            params.is_default = params?.is_default || id === defaultPersonaId || false;
            const result = await dispatch(updatePersona({ id, params })).unwrap();
            setSuccess(true);
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorCode = apiError?.errorCode;
            const moderationErrorMessage = getModerationErrorMessage({
                errorCode,
                skipDefaultMessage: true,
                errorContext: { flow: ModerationFlows.UPDATE_PERSONA },
            });
            const errorMessage = moderationErrorMessage || apiError?.message || t("error:fallbackError");
            setPersonaError({
                errorType: PersonaErrorType.UPDATE_PERSONA,
                errorMessage,
                errorCode,
            });
            personaLogger.error(`Error updating persona: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    };
    const onUserMakePersonaToDefault = async (id) => {
        try {
            const result = await dispatch(makeDefaultPersona(id)).unwrap();
            if (result?.id) {
                logEvent(AnalyticsEvents.PERSONA_DEFAULT, {
                    user_id: userId,
                    persona_id: result.id,
                });
            }
            setSuccess(true);
            return result;
        }
        catch (error) {
            const apiError = error;
            setPersonaError({
                errorType: PersonaErrorType.SET_DEFAULT_PERSONA,
                errorMessage: apiError?.message,
                errorCode: apiError?.errorCode,
            });
        }
    };
    const onUserSelectsPersonaToDelete = async (id) => {
        if (id === defaultPersonaId) {
            setPersonaError({
                errorType: PersonaErrorType.DELETE_DEFAULT_PERSONA,
            });
        }
        // if already selected, remove it
        if (selectedPersonaIds.includes(id)) {
            setSelectedPersonaIds((prev) => prev.filter((i) => i !== id));
        }
        else {
            setSelectedPersonaIds((prev) => [...prev, id]);
        }
    };
    const onUserConfirmSelectedPersonaToChat = useCallback(async () => {
        if (!selectedPersonaIds.length) {
            setPersonaError({
                errorType: PersonaErrorType.NO_PERSONA_SELECTED,
            });
        }
        else {
            const persona = getUserPersonaById(selectedPersonaIds[0]);
            if (persona) {
                dispatch(setSelectedPersona(persona));
                resetSelectedPersonaIds();
            }
        }
    }, [selectedPersonaIds, getUserPersonaById]);
    const onUserConfirmChangePersonaInConversation = useCallback(async (conversationId) => {
        try {
            if (selectedPersonaIds.length) {
                const personaId = selectedPersonaIds[0];
                await dispatch(updatePersonaIdInConversation({
                    personaId: personaId,
                    conversationId,
                })).unwrap();
            }
            else {
                setPersonaError({
                    errorType: PersonaErrorType.NO_PERSONA_SELECTED,
                });
            }
        }
        catch (error) {
            const apiError = error;
            setPersonaError({
                errorType: PersonaErrorType.CHANGE_PERSONA,
                errorMessage: apiError?.message,
                errorCode: apiError?.errorCode,
            });
        }
        finally {
            resetSelectedPersonaIds();
        }
    }, [selectedPersonaIds, getUserPersonaById]);
    const onUserCancelSelectingPersona = () => {
        setSelectedPersonaIds([]);
    };
    const setPersonaToChat = (persona) => {
        if (persona) {
            dispatch(setSelectedPersona(persona));
        }
    };
    const resetSelectedPersonaToChat = () => {
        dispatch(setSelectedPersona(null));
    };
    const resetError = () => {
        if (personaError) {
            setPersonaError(null);
        }
    };
    const resetSuccess = () => {
        if (success) {
            setSuccess(false);
        }
    };
    const resetSelectedPersonaIds = () => {
        setSelectedPersonaIds([]);
    };
    return {
        selectedPersona,
        personas,
        loading,
        isDeletingPersonaStarted,
        selectedPersonaIds,
        personaError,
        defaultPersonaId,
        success,
        setSelectedPersonaIds,
        onUserCreatePersona,
        onUserCreatePersonaV2,
        onUserUpdatePersona,
        onUserUpdatePersonaV2,
        onUserStartDeletingPersona,
        onUserConfirmDeletingPersona,
        onUserCancelDeletingPersona,
        onUserMakePersonaToDefault,
        onUserSelectsPersonaToDelete,
        onUserConfirmSelectedPersonaToChat,
        onUserCancelSelectingPersona,
        getDefaultPersona,
        getUserPersonaById,
        onUserConfirmChangePersonaInConversation,
        resetError,
        resetSelectedPersonaIds,
        setPersonaToChat,
        resetSelectedPersonaToChat,
        resetSuccess,
        errorCode: personaError?.errorCode || null,
        errorMessage: personaError?.errorMessage || null,
        errorType: personaError?.errorType || null,
    };
}
